import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["panel", "button", "quantity", "frame"]
  static values = { open: Boolean }

  // data-turbo-permanent keeps the cart frame alive across navigations (no
  // refetch per page), but also opts it out of Turbo's page morphs — including
  // the refresh="morph" reload pass. So when a morph lands (add-to-cart,
  // quantity updates), reload the frame ourselves; refresh="morph" still makes
  // that reload morph the cart contents in place
  reloadCart() {
    if (this.hasFrameTarget) this.frameTarget.reload()
  }

  // The quantity target arrives with the lazily loaded cart frame. Its
  // refresh="morph" reloads only morph the target's data-quantity attribute
  // (no reconnect), so the frame also triggers this on turbo:frame-load
  updateQuantity() {
    if (!this.hasQuantityTarget) return
    const quantity = Number(this.quantityTarget.dataset.quantity)
    const text = quantity > 0 ? `Cart (${quantity})` : "Cart"
    this.buttonTargets.forEach((button) => (button.textContent = text))
  }

  // Covers the initial load, where the frame can complete before Stimulus boots
  quantityTargetConnected() {
    this.updateQuantity()
  }

  toggle() {
    this.openValue = !this.openValue
  }

  open() {
    this.openValue = true
  }

  close() {
    this.openValue = false
  }

  clickOutside(event) {
    if (!this.openValue) return
    if (this.hasPanelTarget && this.panelTarget.contains(event.target)) return
    if (event.target.closest("[data-action*='shop-cart#']")) return

    this.close()
  }

  // Turbo morphs (add to cart, quantity updates) re-render the body with the
  // server's closed state; keep the open attribute and the panel's inert state
  // so the panel stays open while its contents morph in place
  preserveOpenState(event) {
    const { attributeName } = event.detail

    if (event.target === this.element && attributeName === "data-shop-cart-open-value") {
      event.preventDefault()
    }

    if (this.hasPanelTarget && event.target === this.panelTarget && attributeName === "inert") {
      event.preventDefault()
    }
  }

  openValueChanged(open) {
    this.buttonTargets.forEach((button) => button.setAttribute("aria-expanded", open))
    // The closed panel stays visible on desktop (its divider peeks out), so
    // keep its offscreen content out of the tab order
    if (this.hasPanelTarget) this.panelTarget.inert = !open
  }
}
